"use client";

import { cn } from "@newxsystems/shared";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@templates/design-system/navigation-menu";
import type * as React from "react";
import { resolveKaajPrimitive } from "../../../../client";
import {
  getKaajActiveNavigationItemIndex,
  getKaajPathMatch,
  getLabel,
  hasDropdownContent,
  hasGroupContent,
  isDropdownItem,
  isGroupItem,
  itemKey,
} from "./MegaMenuBlock.shared";
import type {
  MegaMenuDropdown,
  MegaMenuGroup,
  MegaMenuItem,
  MegaMenuLink,
} from "./MegaMenuBlock.props";

const ArrowIcon = () => (
  <svg aria-hidden="true" className="size-3.5 shrink-0" fill="none" viewBox="0 0 16 16">
    <path d="M5 3h8v8" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M13 3 3 13" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const triggerClassName =
  "h-10 rounded-full px-3.5 text-sm font-bold text-[#334155] hover:border-[#D9E1FF] hover:bg-[#F6F8FF] hover:text-[#2144FF] focus-visible:border-[#8FA3FF] focus-visible:ring-[#8FA3FF]/45 data-[state=open]:border-[#D9E1FF] data-[state=open]:bg-[#EEF2FF] data-[state=open]:text-[#2144FF] data-[state=open]:shadow-none";

const ActiveMarker = ({ active }: { active: boolean }) => (
  <span
    aria-hidden="true"
    className={cn("size-1.5 shrink-0 rounded-full bg-[#2144FF] ", active ? "block" : "hidden")}
  />
);

const DesktopMenuLink = ({
  currentPath,
  item,
  quiet = false,
}: {
  currentPath: string;
  item: MegaMenuLink;
  quiet?: boolean;
}) => {
  const Link = resolveKaajPrimitive("Link");
  const isCurrent = getKaajPathMatch(currentPath, item.href) === "page";

  return (
    <NavigationMenuLink asChild>
      <Link
        aria-current={isCurrent ? "page" : undefined}
        aria-label={item.ariaLabel}
        className={cn(
          "group/link flex min-h-12 items-center justify-between gap-4 rounded-2xl border px-4 py-3 text-left transition-[background-color,border-color,box-shadow,color] duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2144FF] motion-reduce:transition-none",
          quiet
            ? "border-transparent text-[#151927] hover:border-[#D9E1FF] hover:bg-white"
            : "border-transparent text-[#151927] hover:border-[#D9E1FF] hover:bg-[#F8FAFF] hover:shadow-[0_10px_24px_rgba(33,68,255,0.08)]",
          isCurrent &&
            "border-[#D9E1FF] bg-[#EEF2FF] shadow-[inset_0_0_0_1px_rgba(33,68,255,0.08)]",
        )}
        href={item.href}
        rel={item.target === "_blank" ? "noopener noreferrer" : undefined}
        target={item.target ?? undefined}
      >
        <span className="min-w-0">
          <span
            className={cn(
              "block text-sm font-extrabold leading-5 tracking-[-0.01em] text-[#151927] transition-colors group-hover/link:text-[#2144FF] [overflow-wrap:anywhere]",
              isCurrent && "text-[#2144FF]",
            )}
          >
            {item.text}
          </span>
        </span>
        <span
          aria-hidden="true"
          className="flex size-7 shrink-0 items-center justify-center rounded-full border border-[#D9E1FF] text-[#2144FF] opacity-0 transition-[opacity,transform] duration-200 group-hover/link:translate-x-0.5 group-hover/link:opacity-100 group-focus-visible/link:opacity-100 motion-reduce:transform-none motion-reduce:transition-none"
        >
          <ArrowIcon />
        </span>
      </Link>
    </NavigationMenuLink>
  );
};

const PanelShell = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <div
    className={cn(
      "overflow-hidden rounded-[28px] border border-[#D9E1FF] bg-white text-[#151927] shadow-[0_26px_78px_rgba(21,25,39,0.14)]",
      className,
    )}
  >
    {children}
  </div>
);

const DropdownPanel = ({ currentPath, item }: { currentPath: string; item: MegaMenuDropdown }) => {
  const links = item.links ?? [];

  if (!hasDropdownContent(item)) return null;

  return (
    <PanelShell className="w-[min(90vw,25rem)] p-3">
      {links.length ? (
        <ul className="grid gap-1">
          {links.map((link, index) => (
            <li key={itemKey("desktop-dropdown", index, link)}>
              <DesktopMenuLink currentPath={currentPath} item={link} />
            </li>
          ))}
        </ul>
      ) : null}
    </PanelShell>
  );
};

const GroupPanel = ({ currentPath, item }: { currentPath: string; item: MegaMenuGroup }) => {
  const groups = item.groups ?? [];
  const label = item.label;

  if (!hasGroupContent(item)) return null;

  return (
    <PanelShell className="w-[min(92vw,48rem)]">
      <div className="grid gap-0 lg:grid-cols-[minmax(14rem,0.72fr)_minmax(0,1.28fr)]">
        <div className="border-b border-[#E6ECFF] bg-[linear-gradient(135deg,#F8FAFF_0%,#FFF7FB_100%)] p-5 lg:border-b-0 lg:border-r">
          {label ? (
            <h3 className="mt-3 text-2xl font-extrabold leading-tight tracking-[-0.04em] text-[#151927]">
              {label}
            </h3>
          ) : null}
        </div>
        <div className="grid gap-3 p-3 sm:grid-cols-2">
          {groups.map((group, groupIndex) => (
            <section
              className="min-w-0 rounded-3xl border border-[#E6ECFF] bg-white p-3"
              key={`${group.header}-${groupIndex}`}
            >
              {group.header ? (
                <h3 className="px-3 py-2 text-xs font-extrabold uppercase tracking-[0.14em] text-[#2144FF] [overflow-wrap:anywhere]">
                  {group.header}
                </h3>
              ) : null}
              {group.links?.length ? (
                <ul className="grid gap-1">
                  {group.links.map((link, linkIndex) => (
                    <li key={itemKey("desktop-group", linkIndex, link)}>
                      <DesktopMenuLink currentPath={currentPath} item={link} quiet />
                    </li>
                  ))}
                </ul>
              ) : null}
            </section>
          ))}
        </div>
      </div>
    </PanelShell>
  );
};

const DesktopDropdownNavItem = ({
  currentPath,
  isCurrent,
  item,
}: {
  currentPath: string;
  isCurrent: boolean;
  item: MegaMenuDropdown;
}) => {
  const label = getLabel(item);

  if (!label || !hasDropdownContent(item)) return null;

  return (
    <NavigationMenuItem>
      <NavigationMenuTrigger
        aria-current={isCurrent ? "location" : undefined}
        className={cn(triggerClassName, "gap-1.5", isCurrent && "bg-[#EEF2FF] text-[#2144FF]")}
      >
        <ActiveMarker active={isCurrent} />
        {label}
      </NavigationMenuTrigger>
      <NavigationMenuContent>
        <DropdownPanel currentPath={currentPath} item={item} />
      </NavigationMenuContent>
    </NavigationMenuItem>
  );
};

const DesktopGroupNavItem = ({
  currentPath,
  isCurrent,
  item,
}: {
  currentPath: string;
  isCurrent: boolean;
  item: MegaMenuGroup;
}) => {
  const label = getLabel(item);

  if (!label || !hasGroupContent(item)) return null;

  return (
    <NavigationMenuItem>
      <NavigationMenuTrigger
        aria-current={isCurrent ? "location" : undefined}
        className={cn(triggerClassName, "gap-1.5", isCurrent && "bg-[#EEF2FF] text-[#2144FF]")}
      >
        <ActiveMarker active={isCurrent} />
        {label}
      </NavigationMenuTrigger>
      <NavigationMenuContent>
        <GroupPanel currentPath={currentPath} item={item} />
      </NavigationMenuContent>
    </NavigationMenuItem>
  );
};

const DesktopLinkNavItem = ({
  currentPath,
  isCurrent,
  item,
}: {
  currentPath: string;
  isCurrent: boolean;
  item: MegaMenuLink;
}) => {
  const Link = resolveKaajPrimitive("Link");
  const pathMatch = isCurrent ? getKaajPathMatch(currentPath, item.href) : undefined;

  return (
    <NavigationMenuItem>
      <NavigationMenuLink asChild>
        <Link
          aria-current={pathMatch}
          aria-label={item.ariaLabel}
          className={cn(
            triggerClassName,
            "inline-flex items-center justify-center gap-1.5",
            pathMatch && "bg-[#EEF2FF] text-[#2144FF]",
          )}
          href={item.href}
          rel={item.target === "_blank" ? "noopener noreferrer" : undefined}
          target={item.target ?? undefined}
        >
          <ActiveMarker active={Boolean(pathMatch)} />
          {item.text}
        </Link>
      </NavigationMenuLink>
    </NavigationMenuItem>
  );
};

const renderDesktopNavigationItems = (items: MegaMenuItem[], currentPath: string) => {
  const activeItemIndex = getKaajActiveNavigationItemIndex(items, currentPath);

  return items.map((item, index) => {
    const isCurrent = index === activeItemIndex;

    if (isDropdownItem(item)) {
      return (
        <DesktopDropdownNavItem
          currentPath={currentPath}
          isCurrent={isCurrent}
          item={item}
          key={`${getLabel(item)}-${index}`}
        />
      );
    }

    if (isGroupItem(item)) {
      return (
        <DesktopGroupNavItem
          currentPath={currentPath}
          isCurrent={isCurrent}
          item={item}
          key={`${getLabel(item)}-${index}`}
        />
      );
    }

    return (
      <DesktopLinkNavItem
        currentPath={currentPath}
        isCurrent={isCurrent}
        item={item as MegaMenuLink}
        key={`${getLabel(item)}-${index}`}
      />
    );
  });
};

export const MegaMenuDesktopNavInner = ({
  currentPath,
  items,
}: {
  currentPath: string;
  items: MegaMenuItem[];
}) => (
  <NavigationMenu aria-label="Primary navigation" className="hidden min-w-0 justify-center lg:flex">
    <NavigationMenuList className="mx-auto rounded-full border border-[#E6ECFF] bg-[#F8FAFF]/82 p-0.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.88)]">
      {renderDesktopNavigationItems(items, currentPath)}
    </NavigationMenuList>
  </NavigationMenu>
);
